import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import makeStyles from "@mui/styles/makeStyles";
import Container from "@mui/material/Container";
import ConfirmDialog from "../components/ConfirmDialog";
import { diffObject, isBlank, notBlank } from "../util/ext";
import { useConfirm } from "../common/use-confirm";
import { clone, lensPath, set } from "ramda";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Box, Card, CardContent, CardHeader } from "@mui/material";
import { validateProgram, validateProgramField } from "./programValidation";
import ProgramProjectContainer from "./ProgramProjectsContainer";
import { useProgram, useProgramUpdate } from "./cspServices";
import { AlertContext, AlertSeverity } from "../components/alert/AlertProvider";
import { useErrorBox } from "../manage-organizations/edit/use-errorBox";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import Typography from "@mui/material/Typography";
import ErrorMessage from "../components/alert/ErrorMessage";
import Header from "../creator-application/common/Header";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OrganizationAutoComplete from "../user-admin/OrganizationAutoComplete";

const useStyles = makeStyles(theme => ({
  root: {
    border: "3px white solid",
    backgroundColor: "white",
    borderRadius: "20px",
  },

  panel: {
    backgroundColor: "white",
    paddingTop: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    maxHeight: "calc(100vh - 300px)",
    minHeight: "calc(100vh - 500px)",
    overflowY: "auto",
  },
}));

const ProgramView = () => {
  const { programId } = useParams();
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const state = useMemo(() => location?.state ?? {}, [location]);

  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;
  const { alertError, showMessage } = useContext(AlertContext);

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    hasValidationError,
    firstValidationError,
    ErrorBox,
  } = useErrorBox();

  const programResponse = useProgram(programId);
  const [program, setProgram] = useState(null);
  const [initial, setInitial] = useState(null);
  useEffect(() => {
    console.log("Checking for success...");
    if (programResponse.isSuccess) {
      setProgram(programResponse.data);
      setInitial(clone(programResponse.data));
    }
  }, [programResponse.isSuccess, programResponse.data]);

  const goBack = useCallback(() => {
    notBlank(state) && notBlank(state.previousLocation)
      ? navigate(state.previousLocation)
      : navigate(`/manage-csp/programs`);
  }, [state, navigate]);

  const handleBackClicked = useCallback(() => {
    const update = diffObject(initial, program);

    if (isBlank(update)) {
      goBack();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have made changes to this program without saving.  Do you wish to discard these changes and go back to the program list?",
        {
          onConfirm: () => {
            goBack();
          },
        },
      );
    }
  }, [program, initial, showConfirm, goBack]);

  const [waiting, setWaiting] = useState(false);
  const disableSave = useCallback(() => {
    return waiting || isBlank(diffObject(initial, program));
  }, [initial, program, waiting]);

  const updateProgram = useProgramUpdate();
  const handleSaveProgram = useCallback(async () => {
    const validationErrors = validateProgram(program);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      setWaiting(true);
      try {
        await updateProgram.mutateAsync({
          resourceIds: { id: programId },
          data: program,
        });
        showMessage("Program Successfully Saved.", AlertSeverity.success);
      } catch (err) {
        alertError(err);
      } finally {
        setWaiting(false);
      }
    }
  }, [
    programId,
    updateProgram,
    program,
    setValidationErrors,
    showMessage,
    alertError,
  ]);

  const handleBlur = (field, value) => {
    const errors = validateProgramField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleTextChange = path => event => {
    const value = notBlank(event.target) ? event.target.value : event;
    setProgram(prev => set(lensPath(path), value, prev));
  };

  return (
    <Container id="program-edit" classes={{ root: classes.root }}>
      {programResponse.isLoading && (
        <Container style={{ height: 400 }}>
          <PanelLoadingIndicator />
        </Container>
      )}
      {programResponse.isError && (
        <Box padding={5} fontSize="1.4rem" fontStyle="italic">
          <Typography variant="h2">Error Loading Program.</Typography>
          <ErrorMessage error={programResponse.error} />
        </Box>
      )}

      {programResponse.isSuccess && notBlank(program) && (
        <Fragment>
          <Header
            title={`Manage ${program.programName} (${program.programId})`}
            selectedActions={
              <Box>
                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackClicked}
                >
                  {notBlank(state) && notBlank(state.label)
                    ? state.label
                    : "Back to Project"}
                </Button>
              </Box>
            }
          />
          <Container classes={{ root: classes.panel }}>
            <Grid container spacing={2}>
              <Grid item sm={4}>
                <Card raised elevation={4} style={{ height: "100%" }}>
                  <CardHeader
                    titleTypographyProps={{
                      variant: "h6",
                      color: "primary",
                    }}
                    title="Details"
                    style={{ paddingTop: 5, paddingBottom: 5 }}
                  />
                  <CardContent style={{ paddingTop: 0 }}>
                    <ErrorBox />
                    <Grid spacing={6} container>
                      <Grid item xs={12}>
                        <TextField
                          id="program-name"
                          fullWidth
                          label="Name"
                          value={program.programName}
                          onChange={handleTextChange(["programName"])}
                          error={hasValidationError("programName")}
                          helperText={firstValidationError("programName")}
                          onBlur={() =>
                            handleBlur("programName", program.programName)
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          id="program-key"
                          fullWidth
                          label="Program Key"
                          value={program.programKey}
                          onChange={handleTextChange(["programKey"])}
                          error={hasValidationError("programKey")}
                          helperText={firstValidationError("programKey")}
                          onBlur={() =>
                            handleBlur("programKey", program.programKey)
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <OrganizationAutoComplete
                          value={program.organizationId}
                          filterFunction={org => org.active}
                          onChange={handleTextChange(["organizationId"])}
                          onBlur={() =>
                            handleBlur("organizationId", program.organizationId)
                          }
                          error={hasValidationError("organizationId")}
                          helperText={firstValidationError("organizationId")}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item sm={8}>
                <Card raised elevation={4} style={{ height: "100%" }}>
                  <CardContent style={{ paddingTop: 0 }}>
                    <ProgramProjectContainer
                      programId={programId}
                      programName={program.programName}
                      programKey={program.programKey}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
          <Box padding={4} display="flex" justifyContent="flex-end">
            <Button
              onClick={handleSaveProgram}
              disabled={disableSave()}
              color="primary"
              variant="contained"
            >
              Save
            </Button>
          </Box>
        </Fragment>
      )}
      <ConfirmDialog handler={confirmHandler} />
    </Container>
  );
};

export default ProgramView;
